/**
 * The noise ring (08 §2.7): how far the ranger's own loudest recent noise carried - footsteps
 * by gait, shots, his own blasts. Radii are the sim's (`NOISE` and the weapon blocks of
 * src/sim/tuning.ts), so the ring is the distance at which a soldier actually hears it. Pure.
 */

import { footstepGait } from '../../../sim/ranger/movement';
import { BOMB, CARBINE, GEAR, GRENADE, LAW } from '../../../sim/tuning';
import type { Biome, SimEvent, SimMods, Ranger, World } from '../../../sim/types';
import { muffle } from '../../../sim/weather';

/** How long a ring stays up, ms: a step is a flicker, a shot or a blast lingers. */
export const STEP_RING_MS = 380;
export const LOUD_RING_MS = 900;
/** A launch this close to the ranger is his own LAW, not a rocket trooper's. */
const OWN_LAUNCH_RADIUS = 1.5;

export interface NoiseSource {
  /** Where the ring is drawn; `follow` rings stay on the ranger instead. */
  x: number;
  z: number;
  follow: boolean;
  /** Metres. */
  radius: number;
  /** `performance.now()` style milliseconds: when it was made, and when it stops showing. */
  at: number;
  until: number;
}

/** The ranger's grenades, bombs and rockets as last seen, to tell his blasts from the enemy's. */
export interface OwnOrdnance {
  x: number;
  z: number;
  /** Metres a blast may be from here and still be this one: a rocket moves between looks. */
  slack: number;
}

/**
 * Radius of the footstep he is making, metres; 0 when he is not moving (movement.ts). R5: the biome
 * muffles it exactly as `makeNoise` does - on snow the ring used to claim 30 % more ground than any
 * soldier could hear over, which is the aid teaching the player a rule the game does not play by.
 */
export function footstepRadius(
  ranger: Pick<Ranger, 'moving' | 'stance' | 'running' | 'sneaking'>,
  mods: Pick<SimMods, 'walkNoise'>,
  biome: Biome,
): number {
  if (!ranger.moving) return 0;
  // ROUND7: the sim's own gait figure, so a sneak draws the 1 m it makes and not a walker's 6 m.
  return footstepGait(ranger, mods) * muffle(biome);
}

/** What is in flight or ticking that a coming blast could belong to. */
export function ownOrdnance(world: World): OwnOrdnance[] {
  const out: OwnOrdnance[] = [];
  // Smoke lands without a bang, so only his fragmentation grenades can own a blast.
  for (const t of world.thrown) if (t.fromPlayer && t.kind === 'grenade') out.push({ x: t.endX, z: t.endZ, slack: 4 });
  for (const b of world.bombs) out.push({ x: b.x, z: b.z, slack: 2 });
  for (const r of world.rockets) out.push({ x: r.x, z: r.z, slack: 10 });
  return out;
}

export interface NoiseContext {
  ranger: Pick<Ranger, 'x' | 'z'>;
  /** From the previous frame: by the time the blast is reported the ordnance is gone. */
  ordnance: readonly OwnOrdnance[];
  now: number;
  /** Snow muffles a shot and a blast as surely as a footstep (`muffle`, src/sim/weather.ts). */
  biome: Biome;
}

/** The ring for one sim event, or null when it was silent or somebody else's. */
export function eventNoise(ev: SimEvent, ctx: NoiseContext): NoiseSource | null {
  const { ranger, now } = ctx;
  const quiet = muffle(ctx.biome);
  const own = (radius: number): NoiseSource => (
    { x: ranger.x, z: ranger.z, follow: true, radius: radius * quiet, at: now, until: now + LOUD_RING_MS }
  );
  switch (ev.type) {
    case 'shot':
      if (!ev.fromPlayer) return null;
      // The event says which gun: after his last pistol round the carbine is already back in his hands.
      return own(ev.suppressed === true ? GEAR.pistolNoise : CARBINE.noise);
    case 'rocket':
      return Math.hypot(ev.x - ranger.x, ev.z - ranger.z) <= OWN_LAUNCH_RADIUS ? own(LAW.noise) : null;
    case 'explosion': {
      const mine = ctx.ordnance.some((o) => Math.hypot(o.x - ev.x, o.z - ev.z) <= o.slack);
      if (!mine) return null;
      const radius = (ev.big ? BOMB.noise : GRENADE.noise) * quiet;
      return { x: ev.x, z: ev.z, follow: false, radius, at: now, until: now + LOUD_RING_MS };
    }
    default:
      return null;
  }
}

/** Drop what has expired; of what is left the loudest shows (newest on a tie), or null. */
export function loudest(sources: NoiseSource[], now: number): NoiseSource | null {
  let best: NoiseSource | null = null;
  for (let i = sources.length - 1; i >= 0; i--) {
    const s = sources[i];
    if (s === undefined || now >= s.until) {
      sources.splice(i, 1);
      continue;
    }
    if (best === null || s.radius > best.radius || (s.radius === best.radius && s.at > best.at)) best = s;
  }
  return best;
}

/** 1 when fresh, falling to 0 as it expires: the ring's opacity. */
export function ringStrength(source: NoiseSource, now: number): number {
  const life = source.until - source.at;
  return life <= 0 ? 0 : Math.min(1, Math.max(0, (source.until - now) / life));
}
